// HowToChatbot - slide-in side panel that lets staff ask questions about
// the LSM 2026 manual. Posts to /api/how-to/chat which forwards the
// question + a packed context (the full topics index + relevant retrieved
// snippets from the manual corpus) to GPT-4.1-mini.
//
// Conversation state lives in this component for the session - it doesn't
// persist across reloads. (If we ever want history, add a small DB table
// and a /api/how-to/chat/history endpoint.)
//
// Citations: the model is asked to wrap topic references as [[topic-id]],
// which we render as inline links.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { TOPICS } from "../lib/howToTopics";
import type { Block } from "../lib/howToTypes";
import { BigIntroOrb } from "../lib/tour";

interface ProposedTicklerAction {
  kind: "create_tickler";
  personId: string;
  personName: string;
  dueDate: string;
  body: string;
}
type ProposedAction = ProposedTicklerAction;

interface Message {
  role: "user" | "assistant";
  content: string;
  /** Optional list of external URLs the model surfaced via web search. */
  webSources?: Array<{ url: string; title: string }>;
  /** Three short follow-up questions the model suggests for this answer.
   *  Rendered as clickable chips below the bubble. */
  followups?: string[];
  /** Action proposals Jack made for this turn - rendered as confirmation
   *  cards under the bubble. Each card has Confirm + Skip buttons; on
   *  Confirm the action runs against the existing CRUD endpoints. */
  proposedActions?: ProposedAction[];
  /** Per-action status keyed by index in proposedActions. Lets the
   *  bubble re-render confirmed / skipped actions without losing the
   *  proposal record. */
  actionStatus?: Record<number, "pending" | "confirming" | "done" | "skipped" | "error">;
  /** When true, the bubble runs a typewriter reveal animation. Cleared
   *  once the full text has been revealed. */
  streaming?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the input with this query when the panel opens. */
  initialQuery?: string;
  /** When true (and initialQuery is set), the chatbot will auto-send the
   *  initialQuery immediately on open - no extra click required. Used
   *  when the user submits via the inline "Need more info? Just ask…"
   *  prompt on a topic page. */
  initialAutoSend?: boolean;
  /** When opened from a topic detail page, the model is told this is the
      topic the user is reading right now. */
  contextTopicId?: string;
}

export function HowToChatbot({ open, onClose, initialQuery, initialAutoSend, contextTopicId }: Props): JSX.Element | null {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Minimized = panel collapsed to a thin header bar pinned to the bottom.
  // No backdrop, page content stays interactive. Click header bar to
  // re-expand; close button still ends the session.
  const [minimized, setMinimized] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Privacy Option 3: when the chatbot is opened from a person's page
  // (/records/:id) we pass that ID to the server so it can inject the
  // participant's PUBLIC-RECORD context (court records, warrants, license
  // status - see src/server/lib/personPublicView.ts for the allowlist).
  // The server also does name resolution on every message, so even off
  // person pages "what's Marcus Williams's status" still works.
  const location = useLocation();
  const personIdContext = useMemo(() => {
    const m = location.pathname.match(/^\/records\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1] ?? "") : null;
  }, [location.pathname]);

  // Cached display name for the active person context - used by the
  // header pill so the LSC can SEE that Jack has this person's record
  // loaded, not just trust that it does. One fetch per personIdContext.
  const [personContextName, setPersonContextName] = useState<string | null>(null);
  useEffect(() => {
    if (!personIdContext) {
      setPersonContextName(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch(`/api/jobs/${personIdContext}`, { credentials: "include" });
        if (!r.ok) return;
        const data = (await r.json()) as { job?: { first_name?: string; last_name?: string } };
        if (cancelled) return;
        const first = data.job?.first_name?.trim() ?? "";
        const last = data.job?.last_name?.trim() ?? "";
        const name = `${first} ${last}`.trim();
        if (name) setPersonContextName(name);
      } catch {
        // ignore - pill just shows generic copy
      }
    })();
    return () => { cancelled = true; };
  }, [personIdContext]);

  // Compact topic index sent to the server with every chat call. ~30KB.
  // Stateless server design - avoids cross-boundary imports between
  // client and server tsconfigs.
  const topicIndex = useMemo(
    () =>
      TOPICS.map((t) => ({
        id: t.id,
        title: t.title,
        summary: t.summary,
        category: t.category,
        source: t.source,
      })),
    [],
  );

  // If we're on a topic detail page, send a flattened version of that
  // topic's full content as additional context so the model can answer
  // follow-ups grounded in what the user is reading.
  const contextTopic = useMemo(() => {
    if (!contextTopicId) return null;
    const t = TOPICS.find((x) => x.id === contextTopicId);
    if (!t) return null;
    return { id: t.id, title: t.title, bodyText: flattenTopicBlocks(t.blocks) };
  }, [contextTopicId]);

  // When opened with a seed query, pre-fill the input. (Don't auto-send
  // unless initialAutoSend is true - let the user see/edit before
  // committing in the default case.)
  useEffect(() => {
    if (open && initialQuery && !initialAutoSend) setInput(initialQuery);
  }, [open, initialQuery, initialAutoSend]);

  // Auto-send when triggered from an inline "Need more info? Just ask…"
  // input on a topic page. Fires once per open with that query.
  const autoSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !initialAutoSend || !initialQuery) return;
    if (autoSentRef.current === initialQuery) return;
    autoSentRef.current = initialQuery;
    void send(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialAutoSend, initialQuery]);
  // Reset auto-send tracking when panel closes so reopening with the
  // same query still fires.
  useEffect(() => {
    if (!open) autoSentRef.current = null;
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Scroll to bottom on new message.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while the chat is open so wheel/trackpad scrolling
  // doesn't bleed through the backdrop and move the page underneath. We
  // preserve and restore the scrollbar gutter so the page doesn't shift
  // horizontally when the lock applies/releases.
  //
  // Also tag the body with `chat-modal-open` so floating UI elsewhere
  // (e.g., the FloatingDock at the bottom of /records/:id) can hide
  // itself via CSS without needing a context plumbed through the tree.
  // The class is removed only when the modal fully closes - a minimized
  // chat keeps the FloatingDock hidden too, since the floating Jack
  // orb at bottom-right would collide with it.
  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add("chat-modal-open");
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.body.classList.remove("chat-modal-open");
    };
  }, [open]);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setError(null);
    setInput("");
    const next: Message[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setBusy(true);
    try {
      const res = await fetch("/api/how-to/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next,
          topicIndex,
          contextTopicId,
          contextTopic,
          personIdContext,
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${body ? ` - ${body.slice(0, 200)}` : ""}`);
      }
      const data: {
        reply: string;
        webSources?: Array<{ url: string; title: string }>;
        followups?: string[];
        proposedActions?: ProposedAction[];
      } = await res.json();
      // Add the message with `streaming: true` so ChatBubble runs the
      // typewriter reveal. The streaming flag is purely cosmetic - the
      // full text is already in `content`, the bubble just doles it out.
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.reply,
          webSources: data.webSources,
          followups: data.followups,
          proposedActions: data.proposedActions,
          streaming: true,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  // When minimized, render only a small floating "alive orb" pill at the
  // bottom-right. Click expands back to the full modal. The full modal
  // takes near-full-screen real estate with a heavy-blur backdrop.
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        aria-label="Resume chat with Jack"
        title="Resume chat with Jack"
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 50,
          width: 76,
          height: 76,
          borderRadius: 999,
          border: "none",
          background:
            "radial-gradient(circle at 35% 30%, #4DA8FF 0%, #1E5BB8 65%, #0D2F66 100%)",
          boxShadow:
            "0 0 24px rgba(77,168,255,0.5), 0 12px 32px rgba(0,0,0,0.55), inset 0 0 16px rgba(255,255,255,0.18)",
          cursor: "pointer",
          padding: 0,
          display: "grid",
          placeItems: "center",
          animation: "jack-floating-pulse 3.6s ease-in-out infinite",
        }}
      >
        <style>{`
          @keyframes jack-floating-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 24px rgba(77,168,255,0.5), 0 12px 32px rgba(0,0,0,0.55), inset 0 0 16px rgba(255,255,255,0.18); }
            50%      { transform: scale(1.06); box-shadow: 0 0 36px rgba(77,168,255,0.75), 0 16px 40px rgba(0,0,0,0.55), inset 0 0 22px rgba(255,255,255,0.25); }
          }
        `}</style>
        <span style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: "radial-gradient(circle at 35% 30%, #ffffff 0%, #BFDDFF 60%, #4DA8FF 100%)",
          boxShadow: "0 0 14px rgba(255,255,255,0.6)",
        }} />
      </button>
    );
  }

  return (
    <>
      {/* Backdrop - warm scrim with a 10px blur. Matches the ⌘K palette
          treatment so the two key entry-point modals share a single
          editorial language. The page goes intentionally soft-focus. */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(26, 19, 14, 0.45)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          zIndex: 40,
          animation: "jack-fade-in 220ms ease",
        }}
      />
      {/* Borderless modal card. Shape defined by a layered close + far
          shadow rather than a stroke. Same recipe as the ⌘K palette. */}
      <aside
        role="dialog"
        aria-label="Jack: NJRC assistant"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(96vw, 1200px)",
          height: "min(82vh, 800px)",
          background: "var(--color-bg-card)",
          border: "none",
          borderRadius: 20,
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 4px 14px rgba(0, 0, 0, 0.18), 0 24px 56px -12px rgba(0, 0, 0, 0.35)",
          animation: "jack-pop-in 320ms cubic-bezier(0.16, 1, 0.3, 1)",
          overflow: "hidden",
        }}
      >
        <style>{`
          @keyframes jack-fade-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes jack-pop-in {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.96); }
            to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
        `}</style>

        {/* Compact hero on top - short header (~80-110px), with the live
            particle orb on the LEFT and the "Ask Jack" title block on
            the RIGHT. Animated cobalt particle field replaces the
            previous solid gradient - feels alive, not flat. */}
        <header
          className="page-hero page-hero-wave jack-hero"
          style={{
            color: "#FFFFFF",
            position: "relative",
            paddingTop: 14,
            paddingBottom: 14,
            paddingLeft: "clamp(16px, 2.4vw, 28px)",
            paddingRight: "clamp(96px, 9vw, 120px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "clamp(12px, 2vw, 28px)",
            flex: "0 0 auto",
            overflow: "hidden",
          }}
        >
          {/* Animated AI-aurora background - deep cobalt base with FIVE
              drifting hue blobs (cyan, violet, magenta, teal, electric
              blue) blending via screen, plus a slow particle dot field.
              Each blob has its own period so the field never repeats
              the same composition. Reads as alive, multi-color, never
              busy. Pure CSS, no canvas. */}
          <span aria-hidden className="jack-bg-base" />
          <span aria-hidden className="jack-bg-blob jack-bg-blob-1" />
          <span aria-hidden className="jack-bg-blob jack-bg-blob-2" />
          <span aria-hidden className="jack-bg-blob jack-bg-blob-3" />
          <span aria-hidden className="jack-bg-blob jack-bg-blob-4" />
          <span aria-hidden className="jack-bg-blob jack-bg-blob-5" />
          <span aria-hidden className="jack-bg-particles" />
          <style>{`
            .jack-hero > *:not([aria-hidden]) { position: relative; z-index: 1; }
            .jack-hero > [aria-hidden] { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
            .jack-hero .jack-bg-base {
              background: linear-gradient(135deg, #15235E 0%, #0A0F3E 60%, #14104A 100%);
            }
            .jack-hero .jack-bg-blob {
              border-radius: 50%;
              filter: blur(48px);
              mix-blend-mode: screen;
              opacity: 0.72;
              will-change: transform;
            }
            /* Each blob = a different hue traveling on its own path. */
            .jack-hero .jack-bg-blob-1 {
              width: 360px; height: 360px;
              top: -140px; left: -40px;
              background: radial-gradient(circle, #4DA8FF 0%, transparent 70%);
              animation: jack-blob-1 17s ease-in-out infinite;
            }
            .jack-hero .jack-bg-blob-2 {
              width: 400px; height: 400px;
              top: -100px; left: 30%;
              background: radial-gradient(circle, #7C5BFF 0%, transparent 70%);
              animation: jack-blob-2 22s ease-in-out infinite;
            }
            .jack-hero .jack-bg-blob-3 {
              width: 320px; height: 320px;
              top: -70px; left: 55%;
              background: radial-gradient(circle, #FF5BD0 0%, transparent 70%);
              animation: jack-blob-3 13s ease-in-out infinite;
            }
            .jack-hero .jack-bg-blob-4 {
              width: 360px; height: 360px;
              top: -90px; right: -30px;
              background: radial-gradient(circle, #2EE6C9 0%, transparent 70%);
              animation: jack-blob-4 19s ease-in-out infinite;
            }
            .jack-hero .jack-bg-blob-5 {
              width: 280px; height: 280px;
              top: -60px; left: 18%;
              background: radial-gradient(circle, #BFDDFF 0%, transparent 70%);
              animation: jack-blob-5 11s ease-in-out infinite;
            }
            @keyframes jack-blob-1 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              33%      { transform: translate(120px, 40px) scale(1.15); }
              66%      { transform: translate(60px, 80px) scale(0.95); }
            }
            @keyframes jack-blob-2 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50%      { transform: translate(-140px, 50px) scale(1.1); }
            }
            @keyframes jack-blob-3 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              40%      { transform: translate(-80px, 60px) scale(1.2); }
              70%      { transform: translate(60px, 30px) scale(0.9); }
            }
            @keyframes jack-blob-4 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50%      { transform: translate(-180px, 30px) scale(1.05); }
            }
            @keyframes jack-blob-5 {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.7; }
              50%      { transform: translate(80px, -20px) scale(1.25); opacity: 0.95; }
            }
            /* Fine particle dot field - sparse stars, very slow drift. */
            .jack-hero .jack-bg-particles {
              background-image:
                radial-gradient(circle 1.2px at 12% 30%, rgba(255,255,255,0.6) 100%, transparent 100%),
                radial-gradient(circle 1px at 28% 65%, rgba(255,255,255,0.5) 100%, transparent 100%),
                radial-gradient(circle 1.4px at 47% 22%, rgba(255,255,255,0.7) 100%, transparent 100%),
                radial-gradient(circle 1px at 62% 80%, rgba(255,255,255,0.45) 100%, transparent 100%),
                radial-gradient(circle 1.6px at 75% 38%, rgba(255,255,255,0.65) 100%, transparent 100%),
                radial-gradient(circle 1px at 88% 60%, rgba(255,255,255,0.5) 100%, transparent 100%),
                radial-gradient(circle 1.2px at 18% 88%, rgba(255,255,255,0.55) 100%, transparent 100%),
                radial-gradient(circle 1px at 55% 50%, rgba(255,255,255,0.4) 100%, transparent 100%),
                radial-gradient(circle 1.3px at 92% 18%, rgba(255,255,255,0.6) 100%, transparent 100%);
              animation: jack-particles-drift 24s linear infinite;
            }
            @keyframes jack-particles-drift {
              0%   { transform: translate(0, 0); opacity: 0.7; }
              50%  { opacity: 1; }
              100% { transform: translate(-40px, 6px); opacity: 0.7; }
            }
            @media (prefers-reduced-motion: reduce) {
              .jack-hero .jack-bg-blob, .jack-hero .jack-bg-particles { animation: none; }
            }
          `}</style>

          {/* Live particle orb - three states with eased color
              transitions:
                idle (blue)      - sitting between turns
                thinking (violet) - waiting for the LLM response
                speaking (gold)   - typewriter is streaming a reply
              Each state has its own palette + the orb amps up speed/
              halo/breath while active. */}
          <div style={{ flex: "0 0 auto" }}>
            <BigIntroOrb
              size={56}
              state={
                busy
                  ? "thinking"
                  : messages.some((m) => m.role === "assistant" && m.streaming)
                    ? "speaking"
                    : "idle"
              }
            />
          </div>

          {/* Right-side title block. "Ask Jack" headline + tagline. */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              textAlign: "right",
            }}
          >
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: "clamp(28px, 3.6vw, 48px)",
                fontWeight: 600,
                lineHeight: 0.95,
                letterSpacing: "-0.02em",
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Ask Jack
            </h2>
            <p
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: "clamp(12px, 1.1vw, 14px)",
                color: "rgba(255,255,255,0.72)",
                marginTop: 4,
                marginBottom: 0,
              }}
            >
              (he's smarter than you)
            </p>
            {personIdContext && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 10,
                  padding: "4px 10px",
                  background: "rgba(45, 138, 79, 0.18)",
                  border: "1px solid rgba(120, 200, 150, 0.42)",
                  borderRadius: 999,
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "rgba(220, 255, 230, 0.94)",
                  fontWeight: 600,
                }}
                title="Jack has this participant's public-record context loaded. Internal notes, contact PII, and clinical strategy are NOT included."
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "#7BD09A",
                    boxShadow: "0 0 8px #7BD09A",
                  }}
                />
                Reading{personContextName ? ` ${personContextName}'s` : ""} public record
              </div>
            )}
          </div>

          {/* Header controls - minimize + close. */}
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              display: "flex",
              gap: 4,
              zIndex: 2,
            }}
          >
            <button
              onClick={() => setMinimized(true)}
              aria-label="Minimize"
              title="Minimize"
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.22)",
                color: "rgba(255,255,255,0.92)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
              </svg>
            </button>
            <button
              onClick={() => { onClose(); setMinimized(false); }}
              aria-label="Close"
              title="Close"
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.22)",
                color: "rgba(255,255,255,0.92)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        {/* Messages - wrapped in a centered container so on ultra-wide
            monitors the chat doesn't stretch to absurd line lengths but
            still uses much more room than the old 540px side panel. */}
        <div ref={scrollerRef} style={{ flex: "1 1 0", minHeight: 0, overflowY: "auto" }}>
          <div
            style={{
              maxWidth: 1100,
              margin: "0 auto",
              padding: "24px clamp(20px, 4vw, 56px)",
            }}
          >
            {messages.length === 0 && <Suggestions onPick={(q) => send(q)} />}
            <div style={{ display: "grid", gap: 18 }}>
              {messages.map((m, i) => {
                const isLastAssistant = m.role === "assistant" && i === messages.length - 1;
                return (
                  <ChatBubble
                    key={i}
                    role={m.role}
                    content={m.content}
                    webSources={m.webSources}
                    followups={isLastAssistant ? m.followups : undefined}
                    onFollowup={(q) => void send(q)}
                    proposedActions={m.proposedActions}
                    actionStatus={m.actionStatus}
                    onConfirmAction={async (idx) => {
                      const action = m.proposedActions?.[idx];
                      if (!action) return;
                      // Mark as confirming
                      setMessages((prev) =>
                        prev.map((pm, pi) => pi === i
                          ? { ...pm, actionStatus: { ...(pm.actionStatus ?? {}), [idx]: "confirming" } }
                          : pm),
                      );
                      try {
                        if (action.kind === "create_tickler") {
                          const r = await fetch(`/api/jobs/${action.personId}/ticklers`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({
                              dueDate: action.dueDate,
                              body: action.body,
                              createdBy: "Jack",
                            }),
                          });
                          if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        }
                        setMessages((prev) =>
                          prev.map((pm, pi) => pi === i
                            ? { ...pm, actionStatus: { ...(pm.actionStatus ?? {}), [idx]: "done" } }
                            : pm),
                        );
                      } catch (err) {
                        setMessages((prev) =>
                          prev.map((pm, pi) => pi === i
                            ? { ...pm, actionStatus: { ...(pm.actionStatus ?? {}), [idx]: "error" } }
                            : pm),
                        );
                        setError(err instanceof Error ? err.message : String(err));
                      }
                    }}
                    onSkipAction={(idx) => {
                      setMessages((prev) =>
                        prev.map((pm, pi) => pi === i
                          ? { ...pm, actionStatus: { ...(pm.actionStatus ?? {}), [idx]: "skipped" } }
                          : pm),
                      );
                    }}
                    streaming={m.streaming}
                    onStreamComplete={() =>
                      setMessages((prev) =>
                        prev.map((pm, pi) => (pi === i ? { ...pm, streaming: false } : pm)),
                      )
                    }
                  />
                );
              })}
              {busy && <ThinkingDots />}
              {error && (
                <div className="card tint-rose" style={{ padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-rose-hi)", marginBottom: 4 }}>
                    Error
                  </div>
                  <div style={{ fontSize: 14, color: "var(--color-text)" }}>{error}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Input - pinned at the bottom of the modal. flex:0 0 auto keeps
            it visible at all viewport sizes. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
          style={{
            flex: "0 0 auto",
            borderTop: "1px solid var(--color-line)",
            background: "var(--color-bg-alt)",
          }}
        >
          <div
            style={{
              maxWidth: 1100,
              margin: "0 auto",
              padding: "16px clamp(20px, 4vw, 56px)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "end" }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send(input);
                  }
                }}
                placeholder="Ask anything: e.g. 'how do I order a Jamaica birth certificate?'"
                rows={2}
                style={{
                  width: "100%",
                  resize: "none",
                  background: "var(--color-bg)",
                  color: "var(--color-text)",
                  border: "1px solid var(--color-line-hi)",
                  borderRadius: 10,
                  padding: "12px 14px",
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  lineHeight: 1.5,
                  outline: "none",
                }}
              />
              <button type="submit" className="btn btn-sage" disabled={busy || !input.trim()} style={{ height: 48, padding: "0 22px", fontSize: 15 }}>
                {busy ? "…" : "Send →"}
              </button>
            </div>
            <div className="meta" style={{ fontSize: 12, marginTop: 8 }}>
              Enter to send · Shift+Enter for newline · Esc to close
            </div>
          </div>
        </form>
      </aside>
    </>
  );
}

// ─── Chat bubble (renders [[topic-id]] citations as inline links) ───────

// ─── Thinking indicator ─────────────────────────────────────────────────
//
// Three dots that bounce in sequence while the model is composing a
// reply. The bubble shape mirrors the assistant's chat bubble so the
// indicator reads as "the next message is on its way" rather than as a
// generic spinner. Kept ~60% opacity so it doesn't compete with the
// final answer once it streams in.
function ThinkingDots(): JSX.Element {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start" }}>
      <div
        aria-label="Jack is thinking"
        role="status"
        style={{
          padding: "16px 20px",
          borderRadius: "14px 14px 14px 4px",
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-line)",
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
        }}
      >
        <span className="cb-think-dot" />
        <span className="cb-think-dot" style={{ animationDelay: "0.18s" }} />
        <span className="cb-think-dot" style={{ animationDelay: "0.36s" }} />
        <style>{`
          .cb-think-dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
            background: var(--color-hero-bg);
            display: inline-block;
            animation: cb-think-bounce 1.1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          @keyframes cb-think-bounce {
            0%, 70%, 100% { transform: translateY(0); opacity: 0.35; }
            35% { transform: translateY(-7px); opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            .cb-think-dot { animation: cb-think-fade 1.4s ease-in-out infinite; transform: none; }
            @keyframes cb-think-fade { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
          }
        `}</style>
      </div>
    </div>
  );
}

function ChatBubble({
  role,
  content,
  pulsing,
  webSources,
  followups,
  onFollowup,
  proposedActions,
  actionStatus,
  onConfirmAction,
  onSkipAction,
  streaming,
  onStreamComplete,
}: {
  role: "user" | "assistant";
  content: string;
  pulsing?: boolean;
  webSources?: Array<{ url: string; title: string }>;
  followups?: string[];
  onFollowup?: (q: string) => void;
  proposedActions?: ProposedAction[];
  actionStatus?: Record<number, "pending" | "confirming" | "done" | "skipped" | "error">;
  onConfirmAction?: (idx: number) => void;
  onSkipAction?: (idx: number) => void;
  streaming?: boolean;
  onStreamComplete?: () => void;
}) {
  const isUser = role === "user";
  // Typewriter reveal - paints `content` chunk-by-chunk for a streaming
  // feel. We tick ~24ms per chunk, with chunks sized so the whole answer
  // reveals in ~1.2-1.8s regardless of length.
  const [revealed, setRevealed] = useState(streaming ? "" : content);
  useEffect(() => {
    if (!streaming) {
      setRevealed(content);
      return;
    }
    setRevealed("");
    const total = content.length;
    if (total === 0) {
      onStreamComplete?.();
      return;
    }
    const targetMs = Math.min(1800, Math.max(800, total * 4));
    const tickMs = 24;
    const ticks = Math.max(8, Math.floor(targetMs / tickMs));
    const perTick = Math.ceil(total / ticks);
    let i = 0;
    const t = setInterval(() => {
      i = Math.min(total, i + perTick);
      setRevealed(content.slice(0, i));
      if (i >= total) {
        clearInterval(t);
        onStreamComplete?.();
      }
    }, tickMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming, content]);
  const showFollowups = !streaming && followups && followups.length > 0 && onFollowup;
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
      <div
        style={{
          maxWidth: isUser ? "75%" : "92%",
          padding: "14px 18px",
          borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          background: isUser ? "var(--color-hero-bg)" : "var(--color-bg-card)",
          border: isUser
            ? "1px solid var(--color-hero-bg)"
            : "1px solid var(--color-line)",
          fontSize: 16,
          lineHeight: 1.6,
          color: isUser ? "var(--color-hero-text, #FFFCF1)" : "var(--color-text)",
          opacity: pulsing ? 0.6 : 1,
          whiteSpace: "pre-wrap",
        }}
      >
        {renderWithCitations(revealed)}
        {streaming && revealed.length < content.length && (
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 8,
              height: 16,
              marginLeft: 2,
              verticalAlign: "text-bottom",
              background: "var(--color-sage-hi)",
              animation: "cbCursor 0.9s steps(2) infinite",
            }}
          />
        )}
        <style>{`@keyframes cbCursor { 50% { opacity: 0; } }`}</style>
        {proposedActions && proposedActions.length > 0 && !streaming && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px dashed var(--color-line-hi)",
              display: "grid",
              gap: 10,
            }}
          >
            {proposedActions.map((a, ai) => {
              const status = actionStatus?.[ai] ?? "pending";
              const isTickler = a.kind === "create_tickler";
              return (
                <div
                  key={ai}
                  style={{
                    padding: "12px 14px",
                    border: "1px solid color-mix(in oklab, var(--color-sage-hi) 35%, var(--color-line))",
                    background: "color-mix(in oklab, var(--color-sage-hi) 8%, transparent)",
                    borderRadius: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        color: "var(--color-sage-hi)",
                      }}
                    >
                      {isTickler ? "↻ Proposed follow-up" : "Proposed action"}
                    </span>
                    {status === "done" && (
                      <span style={{ fontSize: 12, color: "var(--color-sage-hi)" }}>· created ✓</span>
                    )}
                    {status === "skipped" && (
                      <span style={{ fontSize: 12, color: "var(--color-text-dim)" }}>· skipped</span>
                    )}
                    {status === "error" && (
                      <span style={{ fontSize: 12, color: "var(--color-rose-hi)" }}>· failed</span>
                    )}
                  </div>
                  {isTickler && (
                    <>
                      <div style={{ fontSize: 14, lineHeight: 1.4 }}>
                        <span style={{ fontWeight: 600 }}>{a.personName}</span>
                        <span style={{ color: "var(--color-text-dim)" }}> · due </span>
                        <span style={{ fontFamily: "var(--font-mono)" }}>{a.dueDate}</span>
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.45, color: "var(--color-text)" }}>
                        {a.body}
                      </div>
                    </>
                  )}
                  {status === "pending" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                      <button
                        type="button"
                        onClick={() => onConfirmAction?.(ai)}
                        style={{
                          padding: "6px 14px",
                          background: "var(--color-sage-hi)",
                          color: "var(--color-bg)",
                          border: "none",
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => onSkipAction?.(ai)}
                        style={{
                          padding: "6px 12px",
                          background: "transparent",
                          color: "var(--color-text-muted)",
                          border: "1px solid var(--color-line)",
                          borderRadius: 6,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        Skip
                      </button>
                    </div>
                  )}
                  {status === "confirming" && (
                    <div style={{ fontSize: 12, color: "var(--color-text-dim)" }}>Creating…</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {webSources && webSources.length > 0 && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px dashed var(--color-line-hi)",
              fontSize: 12.5,
              color: "var(--color-text-dim)",
            }}
          >
            <div style={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6, fontSize: 11 }}>
              🌐 Web sources
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 4 }}>
              {webSources.map((s, i) => (
                <li key={i}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--color-blue-hi)", textDecoration: "underline", textUnderlineOffset: 3 }}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {showFollowups && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 10,
            maxWidth: "92%",
            animation: "cbFollowupFade 280ms ease-out",
          }}
        >
          <style>{`
            @keyframes cbFollowupFade {
              from { opacity: 0; transform: translateY(4px); }
              to { opacity: 1; transform: none; }
            }
          `}</style>
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--color-text-dim)",
              alignSelf: "center",
              marginRight: 4,
            }}
          >
            Follow-up
          </span>
          {followups!.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onFollowup!(q)}
              style={{
                padding: "7px 13px",
                borderRadius: 999,
                border: "1px solid var(--color-line-hi)",
                background: "var(--color-bg-card-hi)",
                color: "var(--color-text-muted)",
                fontSize: 13.5,
                cursor: "pointer",
                transition: "all 120ms ease",
                fontFamily: "var(--font-body)",
                lineHeight: 1.3,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-sage-hi)";
                e.currentTarget.style.color = "var(--color-text)";
                e.currentTarget.style.background = "var(--color-bg-card)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-line-hi)";
                e.currentTarget.style.color = "var(--color-text-muted)";
                e.currentTarget.style.background = "var(--color-bg-card-hi)";
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TOPIC_BY_ID = new Map(TOPICS.map((t) => [t.id, t]));

// Render LLM reply text with inline rich elements:
//   - [[topic-id]]            → in-app Link to /how-to/:id (topic title)
//   - [text](url)             → external <a> (markdown link)
//   - bare http(s) URLs       → autolinked <a>
//   - **bold**                → <strong>
//   - *italic*                → <em>
//
// The model uses these conventions naturally; rendering them lets links
// actually be clickable instead of leaking as raw text. Bare-URL detect
// is conservative - must start with http:// or https:// so we don't
// accidentally linkify "njcourts.gov" without a scheme.
function renderWithCitations(content: string): React.ReactNode {
  // Single combined regex matching any inline token. Alternation order
  // matters: longest patterns first so e.g. `[[id]]` doesn't get eaten
  // by the bare-link pass.
  const tokenRe = new RegExp(
    "(\\[\\[[a-z0-9-]+\\]\\])"               + // 1. [[topic-id]]
    "|(\\[[^\\]]+\\]\\([^)\\s]+\\))"          + // 2. [text](url)
    "|(https?:\\/\\/[^\\s<>()\\[\\]]+)"      + // 3. bare URL
    "|(\\*\\*[^*\\n]+\\*\\*)"                 + // 4. **bold**
    "|(\\*[^*\\n]+\\*)"                       , // 5. *italic*
    "gi",
  );

  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  const linkStyle: React.CSSProperties = {
    color: "var(--color-sage-hi)",
    textDecoration: "underline",
    textDecorationColor: "rgba(34,197,94,0.4)",
    textUnderlineOffset: 3,
    fontWeight: 600,
  };

  while ((m = tokenRe.exec(content))) {
    if (m.index > last) out.push(content.slice(last, m.index));
    const tok = m[0];

    // 1. Topic citation [[id]]
    if (tok.startsWith("[[")) {
      const id = tok.slice(2, -2);
      const topic = TOPIC_BY_ID.get(id);
      if (topic) {
        out.push(
          <Link key={key++} to={`/how-to/${id}`} style={linkStyle}>
            {topic.title}
          </Link>,
        );
      } else {
        out.push(tok);
      }
    }
    // 2. Markdown link [text](url)
    else if (tok.startsWith("[")) {
      const mdMatch = tok.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (mdMatch) {
        const text = mdMatch[1] ?? "";
        const url = mdMatch[2] ?? "";
        // In-app links (starts with /) → Link; otherwise external.
        if (url.startsWith("/")) {
          out.push(
            <Link key={key++} to={url} style={linkStyle}>
              {text}
            </Link>,
          );
        } else {
          out.push(
            <a key={key++} href={url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {text}
            </a>,
          );
        }
      } else {
        out.push(tok);
      }
    }
    // 3. Bare URL
    else if (/^https?:\/\//i.test(tok)) {
      out.push(
        <a key={key++} href={tok} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {tok}
        </a>,
      );
    }
    // 4. Bold
    else if (tok.startsWith("**")) {
      out.push(
        <strong key={key++} style={{ fontWeight: 700 }}>
          {tok.slice(2, -2)}
        </strong>,
      );
    }
    // 5. Italic
    else if (tok.startsWith("*")) {
      out.push(
        <em key={key++} style={{ fontStyle: "italic" }}>
          {tok.slice(1, -1)}
        </em>,
      );
    }

    last = m.index + tok.length;
  }
  if (last < content.length) out.push(content.slice(last));
  return out;
}

// ─── Topic-blocks → plain text (for chatbot context) ────────────────────

function flattenTopicBlocks(blocks: Block[]): string {
  const out: string[] = [];
  for (const b of blocks) {
    switch (b.kind) {
      case "paragraph":
        out.push(b.text);
        break;
      case "steps":
        if (b.heading) out.push(b.heading);
        b.items.forEach((s, i) => out.push(`${i + 1}. ${s.title}${s.body ? `\n   ${s.body}` : ""}`));
        break;
      case "callout":
        out.push(`[${b.tone.toUpperCase()}${b.title ? `: ${b.title}` : ""}] ${b.text}`);
        break;
      case "checklist":
        if (b.heading) out.push(b.heading);
        b.items.forEach((it) => out.push(`- ${it}`));
        break;
      case "links":
        if (b.heading) out.push(b.heading);
        b.items.forEach((l) => out.push(`- ${l.label}: ${l.href}${l.description ? ` (${l.description})` : ""}`));
        break;
      case "kv":
        if (b.heading) out.push(b.heading);
        b.items.forEach((kv) => out.push(`${kv.label}: ${kv.value}`));
        break;
      case "exhibit":
        if (b.caption) out.push(`[Exhibit: ${b.caption}]`);
        break;
      case "divider":
        if (b.label) out.push(`--- ${b.label} ---`);
        break;
      case "table":
        if (b.heading) out.push(b.heading);
        out.push(b.columns.join(" | "));
        b.rows.forEach((r) => out.push(r.join(" | ")));
        break;
      case "flowchart":
        if (b.heading) out.push(b.heading);
        b.nodes.forEach((n, i) => out.push(`${i + 1}. ${n.title}${n.body ? ` - ${n.body}` : ""}`));
        break;
      case "decisionTree":
        if (b.heading) out.push(b.heading);
        out.push(flattenDecision(b.root));
        break;
      case "timeline":
        if (b.heading) out.push(b.heading);
        b.events.forEach((e) => out.push(`${e.when}: ${e.title}${e.body ? ` - ${e.body}` : ""}`));
        break;
      case "comparison":
        if (b.heading) out.push(b.heading);
        b.options.forEach((o) => {
          out.push(`${o.title}${o.subtitle ? ` (${o.subtitle})` : ""}`);
          o.items.forEach((it) => out.push(`  - ${it}`));
        });
        break;
      case "keyPoints":
        out.push(b.heading || "Key Points");
        b.items.forEach((it) => out.push(`- ${it}`));
        break;
    }
  }
  return out.join("\n\n");
}

function flattenDecision(n: import("../lib/howToTypes").DecisionNode): string {
  if (n.kind === "result") return n.label;
  return `Q: ${n.question} | YES → ${flattenDecision(n.yes)} | NO → ${flattenDecision(n.no)}`;
}

// ─── Inline chat prompt - drop on every topic / index / country page ────
//
// Renders a self-contained "Need more info? Just ask…" input + the
// chatbot side panel. Submitting auto-opens the panel and immediately
// fires the question. Pages just drop <InlineChatPrompt /> wherever
// they want the prompt to appear.

export function InlineChatPrompt({
  contextTopicId,
  variant = "card",
}: {
  /** Pass when used on a topic detail page so the chatbot has full topic context. */
  contextTopicId?: string;
  /** "card" = boxed card with title + description (page-bottom CTA).
   *  "compact" = single-line input only (use mid-page or in tighter spots). */
  variant?: "card" | "compact";
}): JSX.Element {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [sentQuery, setSentQuery] = useState<string | undefined>(undefined);

  function fire(q: string) {
    const t = q.trim();
    if (!t) return;
    setSentQuery(t);
    setOpen(true);
    setInput("");
  }

  const inputBox = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        fire(input);
      }}
      style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}
    >
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Need more info? Just ask…"
        className="input"
        style={{ fontSize: 16, padding: "14px 16px" }}
      />
      <button
        type="submit"
        className="btn btn-sage"
        disabled={!input.trim()}
        style={{ height: 50, padding: "0 22px", fontSize: 15 }}
      >
        Ask →
      </button>
    </form>
  );

  return (
    <>
      {variant === "card" ? (
        <div
          className="card tint-sage"
          style={{ padding: 26 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: "var(--color-sage-hi)",
                color: "var(--color-bg)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 26,
                fontWeight: 700,
                color: "var(--color-text)",
                lineHeight: 1.15,
                fontVariationSettings: '"opsz" 36, "SOFT" 30',
              }}
            >
              Need more info? Just ask.
            </h3>
          </div>
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "var(--color-text-muted)", marginBottom: 18 }}>
            The chatbot has the entire LSM 2026 manual loaded, and can search the web for things the manual
            doesn't cover. It's a senior LSC who never gets tired of questions.
          </p>
          {inputBox}
        </div>
      ) : (
        inputBox
      )}
      <HowToChatbot
        open={open}
        onClose={() => setOpen(false)}
        initialQuery={sentQuery}
        initialAutoSend={true}
        contextTopicId={contextTopicId}
      />
    </>
  );
}

// ─── Suggested questions for the empty state ────────────────────────────

function Suggestions({ onPick }: { onPick: (q: string) => void }) {
  const sugg = [
    "How do I write a time-served letter?",
    "When do I refer to LSNJ vs VLJ vs Public Defender?",
    "What's the difference between DL Suspended and Closed to MVC?",
  ];
  return (
    <div style={{ marginBottom: 24 }}>
      <p
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 22,
          fontWeight: 600,
          color: "var(--color-text)",
          marginBottom: 6,
          lineHeight: 1.3,
          fontVariationSettings: '"opsz" 36, "SOFT" 30',
        }}
      >
        Need more info? Just ask.
      </p>
      <p style={{ fontSize: 15, color: "var(--color-text-muted)", marginBottom: 16, lineHeight: 1.55 }}>
        I'm grounded in the entire LSM 2026 manual. If the answer isn't there, I'll do a web search and tell you so. I refuse to opine on sports, weather, or anything outside NJRC LSC work.
      </p>
      <div className="meta" style={{ marginBottom: 10, fontSize: 12.5 }}>
        Try one of these:
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 10,
        }}
      >
        {sugg.map((q) => (
          <button
            key={q}
            onClick={() => onPick(q)}
            style={{
              textAlign: "left",
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid var(--color-line)",
              background: "var(--color-bg-card)",
              color: "var(--color-text-muted)",
              fontSize: 14.5,
              lineHeight: 1.45,
              cursor: "pointer",
              transition: "all 120ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--color-sage-hi)";
              e.currentTarget.style.color = "var(--color-text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--color-line)";
              e.currentTarget.style.color = "var(--color-text-muted)";
            }}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}
